import type { Subject } from '@/content/schema';
import type { MasteryStatus } from '@/domain/ids';
import { useI18n } from '@/i18n/context';
import { cn } from '@/lib/cn';
import { SHOW_CONTENT_STATUS } from '@/config/content';

export function SubjectBadge({
  subject,
  size = 'md',
}: {
  subject: Pick<Subject, 'glyph' | 'color' | 'teachingLanguage'>;
  size?: 'md' | 'lg';
}) {
  return (
    <span
      aria-hidden
      lang={subject.teachingLanguage}
      className={cn(
        `subject-${subject.color}`,
        'inline-flex shrink-0 items-center justify-center rounded-2xl bg-[var(--subject-soft)] font-black text-[var(--subject-ink)]',
        size === 'lg' ? 'size-16 text-3xl' : 'size-12 text-2xl',
      )}
    >
      {subject.glyph}
    </span>
  );
}

/** Growth stage of an objective in the child's garden. */
export const STAGE: Record<MasteryStatus, { emoji: string; tone: string }> = {
  notStarted: { emoji: '🌰', tone: 'bg-surface-muted text-ink-muted' },
  learning: { emoji: '🌱', tone: 'bg-sky-100 text-sky-700' },
  needsPractice: { emoji: '💧', tone: 'bg-sun-100 text-sun-700' },
  progressing: { emoji: '🌿', tone: 'bg-brand-50 text-brand-800' },
  provisionalMastery: { emoji: '🌻', tone: 'bg-brand-100 text-brand-800' },
};

/** Status shown with an icon and words, never with colour alone. */
export function StatusChip({ status, className }: { status: MasteryStatus; className?: string }) {
  const { t } = useI18n();
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-3 py-0.5 text-sm font-bold',
        STAGE[status].tone,
        className,
      )}
    >
      <span aria-hidden className="emoji">
        {STAGE[status].emoji}
      </span>
      {t(`mastery.status.${status}`)}
    </span>
  );
}

export function SampleBadge({ className }: { className?: string }) {
  const { t } = useI18n();
  if (!SHOW_CONTENT_STATUS) return null;
  return (
    <span
      title={t('app.sampleContentNotice')}
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-sun-100 px-2.5 py-0.5 text-xs font-bold text-sun-700',
        className,
      )}
    >
      <span aria-hidden>🧪</span>
      {t('app.sampleContent')}
    </span>
  );
}

export function Stars({
  count,
  max = 3,
  className,
  label,
}: {
  count: number;
  max?: number;
  className?: string;
  label: string;
}) {
  return (
    <span role="img" aria-label={label} className={cn('inline-flex gap-1', className)}>
      {Array.from({ length: max }, (_, i) => (
        <span
          key={i}
          aria-hidden
          className={cn('emoji animate-pop', i < count ? '' : 'opacity-25 grayscale')}
          style={{ animationDelay: `${i * 140}ms` }}
        >
          ⭐
        </span>
      ))}
    </span>
  );
}
